import { StateSetter, useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, ClientBalanceEntry } from 'entities';

import { Column, Row, Collapse, Tag, IconButton, Button, Card } from 'components/Blocks';

import {
  ChevronDownIcon,
  CircleCheckIcon,
  CircleDollarIcon,
  DollarBillIcon,
  HandCoinsIcon,
  PackageCheckIcon,
  ReceiptLongIcon,
} from 'components/Icons';

import NoBalance from './NoBalance';
import { InvoiceModal } from '../components';

import { useJustification } from 'providers/Justification';

import { getTimestampDisplay } from 'utils/date';
import { cascadeEval } from 'utils/conditions';
import { formatCurrency } from 'utils/text';
import { cn } from 'styles';

interface EntriesProps {
  children?: never;
  entries: ClientBalanceEntry[];
  setEntries: StateSetter<ClientBalanceEntry[]>;
}

function Entries({ entries, setEntries }: EntriesProps): JSX.Element {
  const { t } = useLang();

  const [invoice, setInvoice] = useState<string | null>(null);
  const [expandControl, setExpandControl] = useState<Record<string, boolean>>({});

  const { openJustificationModal } = useJustification();

  const settleBalance = actions.clients.settleBalance.use();

  const settle = (client: string, id: string): void => {
    openJustificationModal({
      text: t('why-are-you-settling-this-balance?'),

      onConfirm: async (justification) => {
        const success = await settleBalance.execute({
          client,
          balanceId: id,
          justification,
        });

        if (!success) return;

        setEntries((old) => old.filter((entry) => entry.id !== id));
      },
    });
  };

  const entriesByCurrency = useMemo(() => {
    const byCountry = entries.reduce(
      (acc, next) => ({
        ...acc,

        [next.currency]: {
          entries: [...(acc[next.currency]?.entries || []), next],

          total:
            next.type === 'OVERPAYMENT'
              ? (acc[next.currency]?.total ?? 0) + Number(next.delta)
              : (acc[next.currency]?.total ?? 0) - Number(next.delta),
        },
      }),
      {} as Record<string, { total: number; entries: ClientBalanceEntry[] }>,
    );

    return byCountry;
  }, [entries]);

  const toggleCurrency = (currency: string): void => {
    setExpandControl((old) => ({
      ...old,
      [currency]: !old[currency],
    }));
  };

  if (!entries.length) return <NoBalance />;

  return (
    <>
      <InvoiceModal invoice={invoice} close={() => setInvoice(null)} />

      {Object.entries(entriesByCurrency).map(([currency, { entries, total }]) => (
        <Column key={currency}>
          <Row
            className="rounded-md bg-primary px-3 py-1 font-medium text-white"
            justifyContent="space-between"
          >
            <Row gap="2">
              <IconButton onClick={() => toggleCurrency(currency)}>
                <ChevronDownIcon
                  className={cn(
                    expandControl[currency] ? 'rotate-180' : '',
                    'transition-transform',
                  )}
                />
              </IconButton>

              {currency}
            </Row>

            {`${total > 0 ? '+' : '-'} ${formatCurrency(Math.abs(total))}`}
          </Row>

          <Collapse expanded={!expandControl[currency]}>
            <Column>
              {entries.map(
                ({
                  id,
                  country,
                  currency,
                  delta,
                  type,
                  invoice,
                  value,
                  valuePaid,
                  registeredAt,
                  client,
                  settled,
                  settledAt,
                  attachedInvoiceId,
                  paidAt,
                  attachedAt,
                  packageId,
                }) => (
                  <Card
                    className="grid grid-cols-[repeat(5,minmax(0,1fr)),200px] gap-4 tablet:grid-cols-2 phone:grid-cols-1"
                    key={id}
                  >
                    <Column fullWidth={false} gap="0">
                      <span
                        className={cn(
                          type === 'OVERPAYMENT' ? 'text-success-light' : 'text-destructive-light',
                          'flex items-center gap-2 text-lg font-semibold',
                        )}
                      >
                        {currency} {type === 'OVERPAYMENT' ? '+' : '-'}
                        {formatCurrency(delta)}
                      </span>

                      <span>{country}</span>
                    </Column>

                    <Row gap="2" className="text-end phone:justify-between">
                      <CircleCheckIcon color="primary-light" size={18} />

                      <Column fullWidth={false} gap="0">
                        <span className="font-medium">{t('registered-at')}</span>

                        <span className="text-sm">{getTimestampDisplay(registeredAt)}</span>
                      </Column>
                    </Row>

                    <Row gap="2" className="text-end phone:justify-between">
                      <HandCoinsIcon color="primary-light" size={18} />

                      <Column fullWidth={false} gap="0">
                        <span className="font-medium">
                          {cascadeEval([
                            { is: attachedAt, then: t('attached-at') },
                            { is: settled, then: t('settled-at') },
                            { is: true, then: t('paid-at') },
                          ])}
                        </span>

                        <span className="text-sm">
                          {getTimestampDisplay(
                            [attachedAt, settledAt, paidAt].find(Boolean) as string,
                          )}
                        </span>
                      </Column>
                    </Row>

                    <Row gap="2" className="text-end phone:justify-between">
                      <DollarBillIcon color="success-light" size={18} />

                      <Column fullWidth={false} gap="0">
                        <span className="font-medium">{t('invoice-value')}</span>

                        <span className="text-sm">{formatCurrency(value, currency)}</span>
                      </Column>
                    </Row>

                    <Row gap="2" className="text-end phone:justify-between">
                      <CircleDollarIcon color="success-light" size={18} />

                      <Column fullWidth={false} gap="0">
                        <span className="font-medium">{t('value-paid')}</span>

                        <span className="text-sm">{formatCurrency(valuePaid, currency)}</span>
                      </Column>
                    </Row>

                    <Row
                      gap="2"
                      justifyContent="flex-end"
                      className="tablet:justify-normal phone:justify-between"
                    >
                      {packageId && (
                        <IconButton
                          tooltipContent={t('view-package') as string}
                          to="packageProfile"
                          params={{ packageId }}
                        >
                          <PackageCheckIcon />
                        </IconButton>
                      )}

                      {invoice && (
                        <IconButton
                          tooltipContent={t('view-invoice') as string}
                          onClick={() => setInvoice(invoice)}
                        >
                          <ReceiptLongIcon />
                        </IconButton>
                      )}

                      {attachedInvoiceId ? (
                        <IconButton
                          tooltipContent={t('view-invoice') as string}
                          onClick={() => setInvoice(attachedInvoiceId)}
                        >
                          <ReceiptLongIcon />
                        </IconButton>
                      ) : settled ? (
                        <Tag color="success-dark" name={t('settled')} />
                      ) : (
                        <Button onClick={() => settle(client, id)} color="secondary">
                          {t('mark-as-settled')}
                        </Button>
                      )}
                    </Row>
                  </Card>
                ),
              )}
            </Column>
          </Collapse>
        </Column>
      ))}
    </>
  );
}

export default Entries;
